"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, Calendar, User, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="h-8 w-32 bg-muted/30 rounded-md animate-pulse mb-8" />
          <div className="h-12 w-3/4 bg-muted/30 rounded-md animate-pulse mb-6" />
          <div className="h-64 bg-muted/30 rounded-lg animate-pulse mb-8" />
          <div className="space-y-4">
            <div className="h-4 bg-muted/30 rounded animate-pulse" />
            <div className="h-4 bg-muted/30 rounded animate-pulse" />
            <div className="h-4 w-5/6 bg-muted/30 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <Card className="bg-destructive/10 border-destructive/20">
            <CardContent className="py-8">
              <p className="text-destructive text-center">
                Error al cargar la publicación. Por favor, inténtalo de nuevo más tarde.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="min-h-screen bg-background py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <Card className="bg-muted/30">
            <CardContent className="py-8">
              <p className="text-muted-foreground text-center">
                No se encontró la publicación.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="min-h-screen bg-background">
      {}
      <div className="bg-muted/30 border-b border-border">
        <div className="container mx-auto px-4 max-w-4xl py-4">
          <Link to="/HomePage" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-[var(--duration-fast)]">
            <ArrowLeft className="size-4" />
            <span>Volver al inicio</span>
          </Link>
        </div>
      </div>

      {}
      <div className="container mx-auto px-4 max-w-4xl py-12 lg:py-16">
        {}
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4 text-primary" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("es-ES", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </time>
            </div>
          )}
          <Separator orientation="vertical" className="h-4" />
          <div className="flex items-center gap-2">
            <Clock className="size-4 text-primary" />
            <span>5 min de lectura</span>
          </div>
        </div>

        {}
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-8 leading-tight">
          {post.title}
        </h1>

        {}
        {post.author && (
          <div className="flex items-center gap-4 mb-10">
            <Avatar className="size-12 border-2 border-primary/20">
              {post.author.avatar?.["96"] && (
                <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
              )}
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                <User className="size-5" />
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-foreground">{post.author.name}</p>
              <p className="text-sm text-muted-foreground">Chef y sommelier</p>
            </div>
          </div>
        )}

        {}
        {post.featuredImage && (
          <div className="relative rounded-lg overflow-hidden mb-10 shadow-strong">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="w-full h-auto object-cover max-h-[600px]"
            />
            <div className="media-overlay" />
          </div>
        )}

        {}
        {post.content && (
          <div
            className="prose prose-invert prose-lg max-w-none mb-12
                       prose-headings:font-display prose-headings:text-foreground
                       prose-p:text-muted-foreground prose-p:leading-relaxed
                       prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                       prose-strong:text-foreground prose-strong:font-semibold
                       prose-blockquote:border-l-4 prose-blockquote:border-primary
                       prose-blockquote:pl-6 prose-blockquote:italic
                       prose-img:rounded-lg prose-img:shadow-medium"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        )}

        <Separator className="my-10" />

        {}
        <div className="space-y-6">
          {post.categories && post.categories.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Categorías
              </h3>
              <div className="flex flex-wrap gap-2">
                {post.categories.map((category) => (
                  <span
                    key={category.id}
                    className="inline-flex items-center px-4 py-2 rounded-md bg-primary/10 text-primary text-sm font-medium border border-primary/20 hover:bg-primary/20 transition-colors duration-[var(--duration-fast)]"
                  >
                    {category.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {post.tags && post.tags.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Etiquetas
              </h3>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center px-3 py-1.5 rounded-md bg-secondary/30 text-foreground text-xs font-medium border border-secondary/40 hover:bg-secondary/50 transition-colors duration-[var(--duration-fast)]"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <Separator className="my-10" />

        {}
        {post.author && (
          <Card className="card">
            <CardContent className="flex flex-col md:flex-row gap-6 items-start">
              <Avatar className="size-20 shrink-0 border-2 border-primary/20">
                {post.author.avatar?.["96"] && (
                  <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
                )}
                <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xl">
                  <User className="size-8" />
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-display text-xl font-bold text-foreground mb-2">
                  Sobre {post.author.name}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Chef apasionado con más de 15 años de experiencia en cocina española tradicional. 
                  Especialista en fusión de sabores canarios con técnicas clásicas españolas.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {}
        <div className="mt-12 text-center">
          <Card className="card bg-primary/5 border-primary/10">
            <CardContent className="py-10">
              <h3 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">
                ¿Te gustó este artículo?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                Visítanos en Bodega Julian para disfrutar de los platillos que inspiraron este contenido. 
                ¡Te esperamos en Calle Mequinez 20, Puerto de la Cruz!
              </p>
              <Button
                asChild
                size="lg"
                className="btn-primary hover-lift"
              >
                <Link to="/HomePage">
                  Ver menú completo
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </article>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <section id="single-post-template-section">
      <PostsProvider postType="post" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}